import config from "appConfig";
import { getLinkForCourseLesson } from "../../../../services/Utils";

const linkLesson0222 = getLinkForCourseLesson(
  "4eb31243-b6cb-423f-919d-15bba103f7ce",
  "e2e93fa9-bdfa-485e-84dd-95f9494dea7d"
);

const theory = ` <h3 class=sectionHead>Introduction</h3>
<p>
   Randomness is omnipresent. Statistics give us a mathematical, logical way to evaluate random processes and to even predict them up to a certain point. With statistics, the most probable value in a certain random process can be calculated. A common example is rolling a die. If we assume a regular die, all six sides have the same probability. The expected value can be calculated by
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>E</mi>
      <mo class=MathClass-open>(</mo>
      <mi>X</mi>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-rel>=</mo>
      <msubsup>
         <mrow>
            <mi class=MathClass-op>∑</mi>
            <mo>⁡</mo>
         </mrow>
         <mrow>
            <mi>i</mi>
            <mo class=MathClass-rel>=</mo>
            <mn>1</mn>
         </mrow>
         <mrow>
            <mi>k</mi>
         </mrow>
      </msubsup>
      <msub>
         <mrow>
            <mi>x</mi>
         </mrow>
         <mrow>
            <mi>i</mi>
         </mrow>
      </msub>
      <msub>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mi>i</mi>
         </mrow>
      </msub>
   </math>
   , where
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mi>i</mi>
         </mrow>
      </msub>
   </math>
   is the probability of case
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>i</mi>
   </math>
   and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>x</mi>
         </mrow>
         <mrow>
            <mi>i</mi>
         </mrow>
      </msub>
   </math>
   is the random variable.
   <figure class=figure>
<p><img alt="Figure 1. Expected value of rolling a normal six-sided die for 500 times with equal probability for all sides." src=${config.ASSETS_URL}/02/25/theory/figure-1.svg><figcaption class=caption>Figure 1. Expected value of rolling a normal six-sided die for 500 times with equal probability for all sides.</figcaption></figure>
<p>
   As shown in <a href="#" data-img-alt="Figure 1. Expected value of rolling a normal six-sided die for 500 times with equal probability for all sides." data-img-src=${config.ASSETS_URL}/02/25/theory/figure-1.svg>Figure 1</a>, the higher the number of rolls, the closer the average converges to the expected value
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>E</mi>
      <mo class=MathClass-open>(</mo>
      <mi>X</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   . If the probability of each number of a die wasn’t equal, the expected value would be different than
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>E</mi>
      <mo class=MathClass-open>(</mo>
      <mi>X</mi>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-rel>=</mo>
      <mn>3</mn>
      <mo class=MathClass-punc>.</mo>
      <mn>5</mn>
   </math>
   . If the probability of rolling the number 6 was twice as high compared to that of the remaining numbers, the expected value would increase to
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>E</mi>
      <mo class=MathClass-open>(</mo>
      <mi>X</mi>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-rel>=</mo>
      <mn>4</mn>
   </math>
   , as illustrated in <a href="#" data-img-alt="Figure 2. Expected value of for up to 500 die rolls using a normal six-sided die with double probability for number 6 compared to the other numbers." data-img-src=${config.ASSETS_URL}/02/25/theory/figure-2.svg>Figure 2</a>.
   <figure class=figure>
<p><img alt="Figure 2. Expected value of for up to 500 die rolls using a normal six-sided die with double probability for number 6 compared to the other numbers." src=${config.ASSETS_URL}/02/25/theory/figure-2.svg><figcaption class=caption>Figure 2. Expected value of for up to 500 die rolls using a normal six-sided die with double probability for number 6 compared to the other numbers.</figcaption></figure>
<p>
   Another important measure in statistics is the standard deviation. The standard deviation indicates the spread of a variable around its mean value. The standard deviation is the average distance of all measured values of a variable from the mean of the distribution. In other words, the faster the curve in <a href="#" data-img-alt="Figure 1. Expected value of rolling a normal six-sided die for 500 times with equal probability for all sides." data-img-src=${config.ASSETS_URL}/02/25/theory/figure-1.svg>Figure 1</a> converges to
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>E</mi>
      <mo class=MathClass-open>(</mo>
      <mi>X</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   , the lower the standard deviation.
<h3 class=sectionHead>Statistics in Listening Experiments</h3>
<p>As introduced in <a href="${linkLesson0222}" target="_blank">Lesson 2.22</a>, the goal of a listening experiment is to answer a research question. One theory, for example, could be that distances of virtual sound sources can be really perceived as different. This claim, also termed a <em><span class=cmti-10>statistical </span><span class=cmti-10>hypothesis</span></em>, could be assessed through an experiment where a pair of sound stimuli is presented, just differing in source distance.
<h4 class=subsectionHead>Hypothesis</h4>
<p>
   By setting up the hypothesis, we are assuming an effect to be present: the perceived distance of both stimuli is different. This is what you want to “accept” and to “conclude”. This is also called the alternative hypothesis
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>H</mi>
         </mrow>
         <mrow>
            <mn>1</mn>
         </mrow>
      </msub>
   </math>
   . It always comes along with the null hypothesis
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>H</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
   </math>
   , which is the opposite of the alternative hypothesis and always states the effect to be absent: the perceived distance of both stimuli is equal. Now, we can test the hypothesis to reject
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>H</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
   </math>
   and to accept
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>H</mi>
         </mrow>
         <mrow>
            <mn>1</mn>
         </mrow>
      </msub>
   </math>
   . However, if you are not able to reject
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>H</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
   </math>
   , it does not mean that you can accept
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>H</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
   </math>
   . You can only state that “there was not enough evidence to reject the null hypothesis”. The best example to understand this complex case would be: just because the prosecution could not prove their case, it does not mean the defendant is innocent.
<p>
   In our example, our two stimuli are presented and the participants have to set the perceived distance for each. After the listening experiment, a set of data with perceived distances for two sound files is available. To prove our research question, the hypothesis
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>H</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
   </math>
   needs to be rejected. Generally speaking, the more participants and the lower the standard deviation, the clearer the difference between both conditions becomes. So to check if
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>H</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
   </math>
   can be rejected, you have to evaluate if the difference between the two results is statistically significant.
<h4 class=subsectionHead>Statistical methods</h4>
<p>To find significant differences in data sets, various methods are established. These methods are evaluating the probability of the results to be statistically significantly different. For this, a common measure used is the p-value, describing the probability of obtaining test results that are at least as extreme as the observed results, while in reality, the effect is not present. Before choosing the correct statistical test, it is necessary to check whether the needed assumptions are fulfilled. Most of the classical statistical tests, which are called parametric tests, require the following assumptions:
<ul class=itemize1>
   <li class=itemize><strong><span class=cmbx-10>normally distributed data</span></strong>
   <li class=itemize><strong><span class=cmbx-10>homogeneity of variance</span></strong> in different conditions the variance remains similar
   <li class=itemize><strong><span class=cmbx-10>independence</span></strong> conditions do not rely on each other
</ul>
<p>For some tests, even more assumptions need to be met, whereas non-parametric tests can be used when certain assumptions are not fulfilled. For details, refer to the individual tests.
<h5 class=subsubsectionHead>t-Test & Analysis of variance (ANOVA)</h5>
<p>In case of one categorical independent variable with two levels, it is possible to conduct a t-test to examine whether the means in the two conditions differ significantly from each other. In our example, the levels would be the two different source distances of the sound files.
<p>Depending on whether you have different participants in each condition or not, you will run the independent t-test (two-sample test) or the dependent t-test (paired-sample t-test). To correctly use the t-test, the above-mentioned assumptions must be met and the dependent data must be collected on a continuous scale. In an optimal case, there should be no outliers, i.e. no individual data points that differ significantly from the others.
<p>If you have more than two levels or more than one categorical independent variables, an Analysis of variance (ANOVA) will be the right choice. It is a combination of data-analytical and structure-testing statistical methods, that compares the statistical effects by analysing the mean values between two or more categorical variables. In order to conduct an ANOVA or one of its many variants, the obtained data needs to fulfill specific conditions. Further information on statistics and its various different methods, interactively demonstrated, can be found, for example, on the Seeing Theory project website [1].
<p>
<h3 class=likesectionHead>Glossary</h3>
<dl>
  <dt>ANOVA
  <dd>Analysis of variance
</dl>
<h3 class=sectionHead>References</h3>
  <dl class=thebibliography>
    <dt>[1]
    <dd>Brown University. Seeing Theory. <a href="https://seeing-theory.brown.edu/" target="_blank">https://seeing-theory.brown.edu/</a>. Accessed on 2021-09-24.
  </dl>`;

export default theory;
